import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { db, collection, query, getDocs, where, getDoc, doc } from '../firebaseConfig';
import { Line } from 'react-chartjs-2';
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import Divider from "@mui/material/Divider";
import AssessmentIcon from '@mui/icons-material/Assessment';
import Tooltip from '@mui/material/Tooltip';
import InfoIcon from '@mui/icons-material/Info';
import { useTheme } from '@mui/material/styles';
import MDTypography from "components/MDTypography";
import { MenuItem, Select } from '@mui/material';

const RiskOverTimeChart = ({ userID, tradeAccountID = 'default' }) => {
  const [trades, setTrades] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [monthlyAvgRisk, setMonthlyAvgRisk] = useState({});
  const [accountName, setAccountName] = useState(tradeAccountID); // Default to ID
  const [capital, setCapital] = useState(0);
  const theme = useTheme();
  const iconColor = theme.palette.mode === 'dark' ? theme.palette.grey[200] : theme.palette.grey[600];
  
    // Fetch account name on mount
    useEffect(() => {
      const fetchAccountName = async () => {
        if (tradeAccountID === 'default') {
          setAccountName('Default');
          return;
        }
        try {
          const accountDoc = await getDoc(doc(db, 'Users', userID, 'tradeAccounts', tradeAccountID));
          if (accountDoc.exists()) {
            setAccountName(accountDoc.data().name || tradeAccountID);
          }
        } catch (error) {
          console.error('Error fetching account name:', error);
        }
      };
      fetchAccountName();
    }, [userID, tradeAccountID]);

  useEffect(() => {
    const fetchCapitalData = async () => {
      try {
        const capitalPath = `Users/${userID}/tradeAccounts/${tradeAccountID}/capital`;
        const capitalQuery = query(collection(db, capitalPath));
        const capitalSnapshot = await getDocs(capitalQuery);
        let totalDeposits = 0;
        let totalWithdrawals = 0;

        capitalSnapshot.forEach((doc) => {
          const data = doc.data();
          totalDeposits += data.deposit || 0;
          totalWithdrawals += data.withdrawal || 0;
        });

        const tradesPath = `Users/${userID}/tradeAccounts/${tradeAccountID}/trades`;
        const tradesQuery = query(collection(db, tradesPath), where('status', '==', 'Closed'));
        const tradesSnapshot = await getDocs(tradesQuery);
        let totalTradeReturns = 0;

        tradesSnapshot.forEach((doc) => {
          const entry = doc.data();
          totalTradeReturns += entry.returns || 0;
        });

        const updatedCapital = totalDeposits - totalWithdrawals + totalTradeReturns;
        setCapital(updatedCapital);
      } catch (error) {
        console.error('Error fetching capital data for RiskOverTimeChart:', error);
      }
    };

    fetchCapitalData();
  }, [userID, tradeAccountID]);

  useEffect(() => {
    const fetchTrades = async () => {
      try {
        const tradesPath = `Users/${userID}/tradeAccounts/${tradeAccountID}/trades`;
        const tradesQuery = query(
          collection(db, tradesPath),
          where('status', '==', 'Closed')
        );
        const tradeSnapshot = await getDocs(tradesQuery);
        const tradesData = tradeSnapshot.docs.map(doc => ({
          id: doc.id,
          accountID: tradeAccountID,
          ...doc.data()
        }));
        setTrades(tradesData);
      } catch (error) {
        console.error('Error fetching trades for RiskOverTimeChart:', error);
      }
    };

    fetchTrades();
  }, [userID, tradeAccountID]);

  useEffect(() => {
    const calculateMonthlyAvgRisk = () => {
      const avgRiskByMonth = {};

      trades.forEach(trade => {
        const riskAmount = parseFloat(trade.riskAmount);
        const tradeDate = new Date(trade.date);
        const month = tradeDate.getMonth() + 1;

        if (tradeDate.getFullYear() === year) {
          if (!avgRiskByMonth[month]) {
            avgRiskByMonth[month] = { totalRisk: 0, numTrades: 0 };
          }
          if (!isNaN(riskAmount) && capital > 0) {
            avgRiskByMonth[month].totalRisk += riskAmount;
            avgRiskByMonth[month].numTrades++;
          }
        }
      });

      for (const month in avgRiskByMonth) {
        if (avgRiskByMonth[month].numTrades > 0) {
          const avgRiskAmount = avgRiskByMonth[month].totalRisk / avgRiskByMonth[month].numTrades;
          avgRiskByMonth[month] = (avgRiskAmount / capital) * 100;
        } else {
          avgRiskByMonth[month] = 0;
        }
      }

      setMonthlyAvgRisk(avgRiskByMonth);
    };

    calculateMonthlyAvgRisk();
  }, [trades, year, capital]);

  const data = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [
      {
        label: 'Monthly Average Risk Per Trade (%)',
        data: Array.from({ length: 12 }, (_, i) => monthlyAvgRisk[i + 1] || 0),
        fill: false,
        backgroundColor: 'rgb(255, 99, 132)',
        borderColor: 'rgba(255, 99, 132, 0.2)',
      },
    ],
  };

  const chartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    scales: {
      y: {
        beginAtZero: true,
        ticks: { callback: function(value) { return `${value.toFixed(2)}%`; } },
      },
    },
  };

  const handleYearChange = (event) => {
    setYear(event.target.value);
  };

  const tooltipContent = `The Risk Over Time Analysis tracks your average risk per trade as a percentage of capital, displaying monthly risk trends across different years. Hover over any point on the chart to view detailed risk stats for that month. Use the year selector to view historical data, with higher percentages indicating larger positions and helping identify risk management patterns. Note: Risk should typically be 1-2% of capital per trade.`;

  return (
    <Card style={{ minHeight: '300px' }}>
      <MDBox display="flex" flexDirection="column" justifyContent="space-between" pt={1} px={2} height="100%">
        <MDBox display="flex" justifyContent="space-between">
          <MDBox
            variant="gradient"
            bgColor="light"
            color="dark"
            coloredShadow="light"
            borderRadius="xl"
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="4rem"
            height="4rem"
            mt={-3}
          >
            <AssessmentIcon fontSize="medium" color="inherit" />
          </MDBox>
          <MDBox textAlign="right" lineHeight={1.25}>
            <MDTypography variant="button" fontWeight="light" color="text" fontFamily="inherit">
              Monthly Average Risk Over Time {tradeAccountID === 'default' ? '' : `(${accountName})`}
            </MDTypography>
            <Tooltip
              title={tooltipContent}
              placement="bottom-end"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    border: `1px solid ${theme.palette.divider}`,
                    p: 1.5,
                    maxWidth: '250px',
                    fontSize: '0.75rem',
                    lineHeight: 1.5,
                    '& .MuiTooltip-arrow': { color: theme.palette.background.paper },
                  }
                }
              }}
            >
              <InfoIcon sx={{ ml: 1, fontSize: '1rem', color: iconColor, cursor: 'help' }} />
            </Tooltip>
          </MDBox>
        </MDBox>
        <Divider />
        <MDBox display="flex" justifyContent="center" mt={2}>
          <Select value={year} onChange={handleYearChange}>
            {[2024, 2025, 2026].map(year => (
              <MenuItem key={year} value={year}>{year}</MenuItem>
            ))}
          </Select>
        </MDBox>
        <MDBox height="100%">
          <Line data={data} options={chartOptions} />
        </MDBox>
      </MDBox>
    </Card>
  );
};

RiskOverTimeChart.propTypes = {
  userID: PropTypes.string.isRequired,
  tradeAccountID: PropTypes.string,
};

export default RiskOverTimeChart;